import { useSyncExternalStore } from 'react';
import type { HexTileMapKey } from '@/game/hexmap';
import type { BuildingType } from '@/game/sim';
import { type Names, NO_NAMES, withName } from '@/game/view/names';
import { NO_SLOTS, type Slots } from '@/game/view/slots';
import type { PendingMove } from '../pendingMove';
import type { Rect } from './boxSelect';

/**
 * The match screen's input state (hovered tile, selection, placement and
 * tile-targeting modes) in one small store, outside React state:
 *
 * - Pointer and key handlers read it directly (`get()`), so they need no ref
 *   mirrors and subscribe once.
 * - Components subscribe to just the fields they show (`useUi`). The hovered
 *   tile changes on nearly every pointer move, and only the hover outline and
 *   the tile info panel (and the HUD, only while a hint depends on it) listen
 *   to it, so hovering doesn't re-render the match screen and its layers.
 */

/**
 * Picking a tile for an order rather than placing a building: a watchtower's
 * watch spot, where a settler goes to found a city, or where the selected
 * fighters attack-move to (any tile; the selection is read at the click).
 */
export type TargetMode =
  | { kind: 'watch'; buildingId: string }
  | { kind: 'found'; unitId: string }
  | { kind: 'attackMove' };

/** The drag payload (comma-separated unit ids) of workers dragged from the dock to a City chip. */
export const WORKERS_MIME = 'application/x-territile-workers';

export interface UiState {
  /** The tile under the cursor (null off the map). */
  hoveredKey: HexTileMapKey | null;
  /** Own units selected (any mix; shift-click adds/removes). */
  selectedUnitIds: readonly string[];
  /** A selected own building; it and the unit selection clear each other. */
  selectedBuildingId: string | null;
  /** The building type being placed. */
  placementMode: BuildingType | null;
  /** The city whose territory placement is limited to. */
  placementCityId: string | null;
  targetMode: TargetMode | null;
  /** A move just ordered, ringed before the host's snapshot confirms it. */
  pendingMove: PendingMove | null;
  /** The player's own names for units, groups and cities, by id (client-local; view/names.ts). */
  names: Names;
  /** The player's control groups, Ctrl+1-9 (client-local; view/slots.ts). */
  slots: Slots;
  /** The entity being renamed inline and which of its labels holds the input (one at a time). */
  renaming: { id: string; site: RenameSite } | null;
  /**
   * Own units to point out: hovering an upgrade card (or its Affects chips)
   * rings them on the map and marks them in the Units list. Empty = none.
   */
  highlightIds: readonly string[];
  /** The Upgrades dock (key Y, or the pill by the timer) is open. */
  upgradesOpen: boolean;
  /** The ⚙ settings panel (hud/SettingsPanel) is open. */
  settingsOpen: boolean;
  /** The Empire drawer (the roster; key E, hud/EmpireDrawer) is open. */
  empireOpen: boolean;
  /** The key legend (key ?, hud/KeyLegend) is open. */
  legendOpen: boolean;
  /** The box-select marquee being dragged, in client pixels. */
  marquee: Rect | null;
  /** Workers being dragged from the dock toward a City chip, to re-home. */
  draggingWorkers: readonly string[] | null;
}

/** Where an inline rename input shows: the dock title, a roster row, a city chip or a groups bar tile. */
export type RenameSite = 'dock' | 'row' | 'chip' | 'slot';

export type UiPatch = Partial<UiState> | ((state: UiState) => Partial<UiState>);

export interface UiStore {
  get: () => UiState;
  /** Merges `patch`; listeners hear of it only when a field really changed. */
  set: (patch: UiPatch) => void;
  subscribe: (listener: () => void) => () => void;
}

/** `highlightIds` when nothing is pointed at (one array, so setting it again is no change). */
export const NO_HIGHLIGHT: readonly string[] = [];

export const INITIAL_UI: UiState = {
  hoveredKey: null,
  selectedUnitIds: [],
  selectedBuildingId: null,
  placementMode: null,
  placementCityId: null,
  targetMode: null,
  pendingMove: null,
  names: NO_NAMES,
  slots: NO_SLOTS,
  renaming: null,
  highlightIds: NO_HIGHLIGHT,
  upgradesOpen: false,
  settingsOpen: false,
  empireOpen: true,
  legendOpen: false,
  marquee: null,
  draggingWorkers: null,
};

export function createUiStore(initial: UiState = INITIAL_UI): UiStore {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set(patch) {
      let changes = typeof patch === 'function' ? patch(state) : patch;
      // A rename input is for the selected entity: another selection closes it.
      if (
        state.renaming &&
        !('renaming' in changes) &&
        ('selectedUnitIds' in changes || 'selectedBuildingId' in changes) &&
        (!Object.is(changes.selectedUnitIds ?? state.selectedUnitIds, state.selectedUnitIds) ||
          !Object.is(
            changes.selectedBuildingId ?? state.selectedBuildingId,
            state.selectedBuildingId
          ))
      ) {
        changes = { ...changes, renaming: null };
      }
      let changed = false;
      for (const key of Object.keys(changes) as Array<keyof UiState>) {
        if (!Object.is(changes[key], state[key])) {
          changed = true;
          break;
        }
      }
      if (!changed) return;
      state = { ...state, ...changes };
      for (const listener of listeners) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * The part of the store `select` picks, re-rendering only when it changes
 * (by `Object.is`). `select` must return a field or a primitive (not a new
 * object each call).
 */
export function useUi<T>(store: UiStore, select: (state: UiState) => T): T {
  const snapshot = () => select(store.get());
  return useSyncExternalStore(store.subscribe, snapshot, snapshot);
}

/** A `useState`-style setter for one field (takes a value or an updater). */
export function fieldSetter<K extends keyof UiState>(
  store: UiStore,
  key: K
): (next: UiState[K] | ((prev: UiState[K]) => UiState[K])) => void {
  return (next) =>
    store.set((s) => ({
      [key]: typeof next === 'function' ? (next as (prev: UiState[K]) => UiState[K])(s[key]) : next,
    }));
}

/** Opens the inline rename input for `id` at `site`. */
export function startRename(store: UiStore, id: string, site: RenameSite) {
  store.set({ renaming: { id, site } });
}

/** Saves `raw` as `id`'s name (empty clears it) and closes the input. */
export function commitRename(store: UiStore, id: string, raw: string) {
  store.set((s) => ({ names: withName(s.names, id, raw), renaming: null }));
}

/** Closes the rename input, keeping the old name. */
export function cancelRename(store: UiStore) {
  store.set({ renaming: null });
}
